import { useCallback, useEffect, useRef, useState } from 'react';
// hooks
import useAuth from './useAuth';
// utils
import axiosInstance from '../utils/axios';
import { APIS } from '../utils/serviceUrls';
// routes
import { PATH_DASHBOARD } from '../routes/paths';

// ----------------------------------------------------------------------
// Real admin notifications (UI/UX plan §6, Step 1 — FE polling, no BE feed yet).
//
// Each "source" maps an EXISTING, role-authorized endpoint to a single actionable
// notification card. Sources are gated to the roles whose BE @Auth already allows
// the call, so a poll never produces a 403 (super-admin inherits everything):
//   - signups / at-risk / pending-grading : admin, super-admin
//   - payments / expiring-subscriptions   : admin, super-admin, sales
//   - each list must match the BE @Auth for that endpoint, or the role fires a
//     request it is guaranteed to get a 403 from.
//
// Cards are counters (not an event stream), stamped with start-of-today so they
// re-alert once per day rather than on every 60s poll. Unread = cards newer than
// the locally-stored "last seen" timestamp; "mark all read" advances it.
// ----------------------------------------------------------------------

export type AppNotification = {
  id: string;
  title: string;
  description: string;
  count: number;
  createdAt: string; // ISO
  href: string;
  icon: string; // iconify name
  color: 'primary' | 'info' | 'success' | 'warning' | 'error';
};

type Source = {
  id: string;
  roles: string[];
  icon: string;
  color: AppNotification['color'];
  href: string;
  // returns [count, title, description] or null on failure
  load: () => Promise<{ count: number; title: string; description: string } | null>;
};

const POLL_MS = 60_000;
// Per-user, per-item read state. Keyed per user so it can't leak across accounts
// that share a browser. We store a map { notificationId -> signature }: clicking
// one notification marks only THAT id read (badge -1), and the signature
// (date + count) makes a card go unread again when a new day starts or its count
// changes — so handled items don't re-alert, but fresh ones do.
const READ_PREFIX = 'kayfa.notifications.read';
const readKey = (userId: string) => `${READ_PREFIX}:${userId}`;
const signature = (n: AppNotification) => `${n.createdAt}|${n.count}`;

const unwrap = (res: any) => res?.data?.data ?? res?.data ?? {};

function startOfTodayISO(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

const SOURCES: Source[] = [
  {
    id: 'expiring',
    roles: ['admin', 'super-admin', 'sales', 'sales-manager'],
    icon: 'ant-design:clock-circle-outlined',
    color: 'warning',
    href: PATH_DASHBOARD.userSegments,
    load: async () => {
      const d = unwrap(
        await axiosInstance.get(APIS.ADMIN_STATS.UPCOMING_EXPIRATIONS, {
          params: { days: 7 },
        }),
      );
      const count = Number(d?.count ?? 0);
      if (!count) return null;
      return {
        count,
        title: `${count} subscription${count === 1 ? '' : 's'} expiring within 7 days`,
        description: 'Review renewals in User Segments',
      };
    },
  },
  {
    id: 'payments',
    roles: ['admin', 'super-admin', 'sales-manager'],
    icon: 'ant-design:credit-card-outlined',
    color: 'success',
    href: PATH_DASHBOARD.payments,
    load: async () => {
      const d = unwrap(
        await axiosInstance.get(APIS.PAYMENTS.LIST, {
          params: { dateFrom: startOfTodayISO(), page: 1, pageSize: 1 },
        }),
      );
      const count = Number(d?.total ?? 0);
      if (!count) return null;
      return {
        count,
        title: `${count} payment${count === 1 ? '' : 's'} today`,
        description: 'Open the Payments list',
      };
    },
  },
  {
    id: 'signups',
    roles: ['admin', 'super-admin', 'sales-manager'],
    icon: 'ant-design:user-add-outlined',
    color: 'info',
    href: PATH_DASHBOARD.user.list,
    load: async () => {
      const d = unwrap(
        await axiosInstance.get(APIS.ADMIN_STATS.SIGNUPS, { params: { days: 1 } }),
      );
      const count = Number(d?.inWindow ?? 0);
      if (!count) return null;
      return {
        count,
        title: `${count} new signup${count === 1 ? '' : 's'} today`,
        description: 'View the Students list',
      };
    },
  },
  {
    id: 'atRisk',
    roles: ['admin', 'super-admin', 'customer-care', 'support'],
    icon: 'ant-design:warning-outlined',
    color: 'error',
    href: PATH_DASHBOARD.studentAlerts,
    load: async () => {
      const d = unwrap(await axiosInstance.get(APIS.STUDENT_ALERTS.SUMMARY));
      const count = Number(d?.withAlerts ?? 0);
      if (!count) return null;
      return {
        count,
        title: `${count} student${count === 1 ? '' : 's'} need attention`,
        description: 'Open Student Alerts',
      };
    },
  },
  {
    id: 'grading',
    roles: ['admin', 'super-admin'],
    icon: 'ant-design:file-done-outlined',
    color: 'primary',
    href: PATH_DASHBOARD.finalProjects,
    load: async () => {
      const d = unwrap(
        await axiosInstance.get(APIS.FINAL_PROJECTS.LIST, {
          params: { page: 1, pageSize: 100 },
        }),
      );
      const items: any[] = Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : [];
      const count = items.reduce((s, p) => s + Number(p?.pendingGrading ?? 0), 0);
      if (!count) return null;
      return {
        count,
        title: `${count} final-project submission${count === 1 ? '' : 's'} awaiting grading`,
        description: 'Open Final Projects',
      };
    },
  },
];

function readReadMap(userId: string): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(readKey(userId)) || '{}') || {};
  } catch {
    return {};
  }
}

export default function useNotifications() {
  const { user } = useAuth();
  const roles: string[] = ((user as any)?.roles as string[]) ?? [];
  const isSuperAdmin = roles.includes('super-admin');
  // Identify the signed-in account so read state is scoped to them.
  const userId = String((user as any)?._id || (user as any)?.email || 'anon');

  const [items, setItems] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  // { notificationId -> signature } of items this user has read.
  const [readMap, setReadMap] = useState<Record<string, string>>(() => readReadMap(userId));

  // Re-read the per-user read map whenever the signed-in account changes
  // (e.g. logging out and back in as a different staff member same browser).
  useEffect(() => {
    setReadMap(readReadMap(userId));
  }, [userId]);
  // Keep the latest role list in a ref so the polling effect doesn't re-subscribe.
  const allowedRef = useRef<Source[]>([]);
  allowedRef.current = SOURCES.filter(
    (s) => isSuperAdmin || s.roles.some((r) => roles.includes(r)),
  );

  const refresh = useCallback(async () => {
    const sources = allowedRef.current;
    if (!user || sources.length === 0) {
      setItems([]);
      return;
    }
    setLoading(true);
    const createdAt = startOfTodayISO();
    const results = await Promise.allSettled(sources.map((s) => s.load()));
    const next: AppNotification[] = [];
    results.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value) {
        const s = sources[i];
        next.push({
          id: s.id,
          title: r.value.title,
          description: r.value.description,
          count: r.value.count,
          createdAt,
          href: s.href,
          icon: s.icon,
          color: s.color,
        });
      }
    });
    setItems(next);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const persist = useCallback(
    (map: Record<string, string>) => {
      try {
        localStorage.setItem(readKey(userId), JSON.stringify(map));
      } catch {
        /* ignore storage failures */
      }
    },
    [userId],
  );

  const isUnread = useCallback(
    (n: AppNotification) => readMap[n.id] !== signature(n),
    [readMap],
  );

  const unreadCount = items.filter(isUnread).length;

  // Mark a SINGLE notification read (badge -1) — used when one is clicked.
  const markRead = useCallback(
    (n: AppNotification) => {
      setReadMap((prev) => {
        const next = { ...prev, [n.id]: signature(n) };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const markAllAsRead = useCallback(() => {
    setReadMap((prev) => {
      const next = { ...prev };
      items.forEach((n) => {
        next[n.id] = signature(n);
      });
      persist(next);
      return next;
    });
  }, [items, persist]);

  return { items, unreadCount, loading, markRead, markAllAsRead, isUnread, refresh };
}
